import {queryOptions} from '@tanstack/react-query'
import {notFound} from '@tanstack/react-router'
import {createServerFn} from '@tanstack/start'
import {z} from 'zod'
import fetchProductFilters from '~/client/spreadshop/old/fetchProductFilters'
import {applyServerFnCache} from '~/client/fn/utils'
import {Locale} from '~/gql/graphql'

const CACHE_TIME_SEC = 60 * 60 * 24 // 24h

const productFilterParams = z
    .object({
        locale: z.nativeEnum(Locale).optional(),
        departmentId: z.string().optional(),
        categoryId: z.string().optional(),
        appearanceId: z.string().optional(),
        sizeId: z.string().optional(),
        ideaId: z.string().optional(),
        collectionId: z.string().optional(),
    })
    .optional()

export const getProductFilter = createServerFn({
    method: 'GET',
})
    .validator((data: unknown) => {
        const result = productFilterParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }

        return result.data
    })
    .handler(async ({data}) => {
        const {locale, departmentId, categoryId, appearanceId, sizeId, ideaId, collectionId} =
            data ?? {}

        const filters = await fetchProductFilters(locale, {
            departmentId,
            categoryId,
            appearanceId,
            sizeId,
            ideaId,
            collectionId,
        })

        const filterOptions = filters.shop?.filterOptions
        if (!filterOptions) {
            return undefined
        }

        applyServerFnCache(CACHE_TIME_SEC)

        return filterOptions
    })

export const productFilterQueryOptions = (
    departmentId?: string,
    categoryId?: string,
    appearanceId?: string,
    sizeId?: string,
    ideaId?: string,
    collectionId?: string,
    locale: Locale = Locale.EnEu
) =>
    queryOptions({
        queryKey: [
            locale,
            'product-filters',
            ideaId ? ideaId : 'without-idea',
            collectionId ? collectionId : 'without-collection',
            departmentId ?? '',
            categoryId ?? '',
            appearanceId ?? '',
            sizeId ?? '',
        ],
        gcTime: 1000 * CACHE_TIME_SEC, // 24h
        staleTime: 1000 * CACHE_TIME_SEC, // 24h
        queryFn: () =>
            getProductFilter({
                data: {
                    departmentId,
                    categoryId,
                    appearanceId,
                    sizeId,
                    ideaId,
                    collectionId,
                    locale,
                },
            }),
        placeholderData: (previousData) => previousData,
    })
